import React, { useCallback, useEffect, useRef, useState } from "react";
import { FixedSizeList } from "react-window";
import ChatListRow from "./ChatListRow";

const ROW_HEIGHT = 73;

// Renders the chat list virtualized so opening the app with hundreds/thousands of chats
// (now that useChatSessions no longer caps at 50) doesn't mount that many DOM rows at once.
const VirtualizedChatList = React.memo(function VirtualizedChatList({
  chats,
  selectedChatId,
  formatChatListTime,
  t,
  onSelect,
  onDelete,
}) {
  const containerRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setSize((prev) => (prev.width === width && prev.height === height ? prev : { width, height }));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const itemKey = useCallback((index) => chats[index]?.id ?? index, [chats]);

  const Row = useCallback(
    ({ index, style }) => {
      const chat = chats[index];
      if (!chat) return null;
      return (
        <div style={style}>
          <ChatListRow
            chat={chat}
            selected={selectedChatId === chat.id}
            formatChatListTime={formatChatListTime}
            t={t}
            onSelect={onSelect}
            onDelete={onDelete}
          />
        </div>
      );
    },
    [chats, selectedChatId, formatChatListTime, t, onSelect, onDelete],
  );

  return (
    <div ref={containerRef} style={{ flex: 1, minHeight: 0 }}>
      {size.width > 0 && size.height > 0 && (
        <FixedSizeList
          height={size.height}
          width={size.width}
          itemCount={chats.length}
          itemSize={ROW_HEIGHT}
          itemKey={itemKey}
          overscanCount={8}
        >
          {Row}
        </FixedSizeList>
      )}
    </div>
  );
});

export default VirtualizedChatList;
